// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Bundle } from '@/bundle/api/types';
import {
  ApplyBundleDialog,
  BundleApplyLine,
} from '@/bundle/components/apply-bundle-dialog';
import { buildBundleAppliedToast } from '@/bundle/utilities/bundle-applied-toast';
import { computePhysicalBundleApply } from '@/bundle/utilities/bundle-physical-apply';
import { DateShortcutButtons } from '@/components/date-shortcut-buttons';
import { FileDropZone, UploadingFile } from '@/components/file-drop-zone';
import { FormDatePicker } from '@/components/form-date-picker';
import { FormNumberTextField } from '@/components/form-number-text-field';
import { FormShippingCarrierSearch } from '@/components/form-shipping-carrier-search';
import { FormTextField } from '@/components/form-text-field';
import {
  PROMISE_SHIP_DATE_SHORTCUTS,
  getPromiseDeliveryDateShortcuts,
} from '@/constants/date-shortcut';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  InfoOutlined,
  LibraryAddOutlined,
} from '@/constants/hardfin-standard-icons';
import { ShippingCarrier } from '@/constants/shipping-carrier';
import { useToast } from '@/contexts/toast-context';
import { useDisclosure } from '@/hooks/use-disclosure';
import { ItemSearch } from '@/items/components/item-search';
import { FormLocationSearch } from '@/location/components/form-location-search';
import { getNewLocationURL } from '@/location/utilities/get-new-location-url';
import { ItemFormEntry } from '@/orders/schemas/item';
import {
  RETURN_ORDER_REASON_LABELS,
  ReturnOrder,
  returnOrderReasonSchema,
} from '@/return-order/api/types';
import { isProcessed as isProcessedStatus } from '@/return-order/constants/status';
import { useReturnOrderInventory } from '@/return-order/hooks/use-return-order-inventory';
import {
  RETURN_ORDER_MAX_ITEMS,
  ReturnOrderFormData,
  returnOrderFormSchema,
} from '@/return-order/schemas/return-order-form-schema';
import { SparseItem, SparseLocation } from '@/sparse/api/types';
import { formatNumber } from '@/utilities/number';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Collapse,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';

interface ReturnOrderFormProps {
  /**
   * Optional return order to edit. If provided, the form will be in edit mode
   * with pre-populated values.
   */
  returnOrder?: ReturnOrder;

  /**
   * Callback fired when form is submitted with valid data.
   * The parent component is responsible for handling the data (API calls, etc.).
   */
  onSubmit: (data: ReturnOrderFormData) => void;

  /**
   * The form ID used to connect the external submit button to this form.
   * The dialog wrapper provides this ID and uses it on its submit button.
   */
  formId: string;

  /**
   * Optional initial form data to pre-populate the form.
   * Used by exchange order dialog to restore form state when navigating back.
   */
  initialFormData?: ReturnOrderFormData;

  /**
   * The organization's default return location, used to seed the to location
   * on new orders so users don't re-pick the primary warehouse every time. Only
   * applied on create when nothing else provides a destination; an archived
   * default is treated as unset.
   */
  orgDefaultReturnLocation?: SparseLocation | null;

  /**
   * Optional file upload props for the file drop zone.
   * When provided, shows a file upload section below the notes field.
   */
  fileUploadProps?: {
    files: UploadingFile[];
    onFilesSelected: (files: File[]) => void;
    onRemoveFile: (localId: string) => void;
    disabled?: boolean;
  };
}

type FormData = ReturnOrderFormData;

const defaultValues: FormData = {
  returnType: null,
  returnReason: null,
  fromLocation: null,
  toLocation: null,
  promiseShipDate: null,
  promiseDeliveryDate: null,
  orderNotes: null,
  carrier: null,
  referenceNumber: null,
  otherCarrier: null,
  items: [
    {
      item: null,
      quantity: 1,
    },
  ],
};

/**
 * Pure form component for creating or editing a return order.
 * Handles form state and validation only - the parent component
 * is responsible for API calls and side effects.
 */
export function ReturnOrderForm(props: ReturnOrderFormProps) {
  const isEditMode = !!props.returnOrder;

  // Determine which fields should be disabled based on return order status
  const isProcessed = props.returnOrder
    ? isProcessedStatus(props.returnOrder.status)
    : false;

  const getDefaultValues = useCallback((): FormData => {
    // Use initialFormData if provided (for restoring form state when navigating back)
    if (props.initialFormData) {
      return props.initialFormData;
    }

    if (isEditMode && props.returnOrder) {
      return {
        returnType: props.returnOrder.reason,
        returnReason: props.returnOrder.returnReason ?? null,
        fromLocation: props.returnOrder.fromLocation,
        toLocation: props.returnOrder.toLocation,
        promiseShipDate: props.returnOrder.promiseShipDate,
        promiseDeliveryDate: props.returnOrder.promiseDeliveryDate,
        orderNotes: props.returnOrder.orderNotes,
        carrier: props.returnOrder.transportReferenceDetails?.carrier ?? null,
        referenceNumber:
          props.returnOrder.transportReferenceDetails?.transportReference ??
          null,
        otherCarrier: props.returnOrder.transportReferenceDetails?.name ?? null,
        items: props.returnOrder.items.map((lineItem) => ({
          item: lineItem.item,
          quantity: lineItem.requestedQuantity,
        })),
      };
    }
    const baseValues = { ...defaultValues };
    if (
      props.orgDefaultReturnLocation &&
      !props.orgDefaultReturnLocation.isArchived
    ) {
      baseValues.toLocation = props.orgDefaultReturnLocation;
    }
    return baseValues;
  }, [
    isEditMode,
    props.returnOrder,
    props.initialFormData,
    props.orgDefaultReturnLocation,
  ]);

  const { control, handleSubmit, setValue, getValues } = useForm<FormData>({
    resolver: zodResolver(returnOrderFormSchema),
    defaultValues: getDefaultValues(),
  });

  const applyBundleDialog = useDisclosure();
  const { showToast } = useToast();

  // Watch the promise ship date to anchor the delivery date shortcuts
  const selectedPromiseShipDate = useWatch({
    control,
    name: 'promiseShipDate',
  });

  const hasExistingTransportRef =
    !!props.returnOrder?.transportReferenceDetails?.carrier ||
    !!props.returnOrder?.transportReferenceDetails?.transportReference;
  const [isTransportRefOpen, setIsTransportRefOpen] = useState(
    hasExistingTransportRef
  );

  const watchedCarrier = useWatch({
    control,
    name: 'carrier',
  });
  const isOtherCarrier = watchedCarrier === ('other' as ShippingCarrier);

  const handleCloseTransportRef = () => {
    setValue('carrier', null);
    setValue('referenceNumber', null);
    setValue('otherCarrier', null);
    setIsTransportRefOpen(false);
  };

  const currentItems = useWatch({
    control,
    name: 'items',
  });

  const watchedType = useWatch({
    control,
    name: 'returnType',
  });

  const selectedFromLocation = useWatch({
    control,
    name: 'fromLocation',
  });

  // Fetch inventory for the from location (includes non-inventory locations)
  const {
    inventoryMap,
    isLoading: isLoadingInventory,
    isFetching: isFetchingInventory,
  } = useReturnOrderInventory({
    location: selectedFromLocation,
  });

  const {
    fields: itemsField,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'items',
  });

  const handleAddItem = () => {
    append({ item: null, quantity: 1 });
  };

  // Return orders forbid duplicate SKU rows and hold only physical items, so
  // applying a bundle sums each physical member's computed quantity into an
  // existing row, appends members with no existing row (up to the cap), and
  // drops service members.
  const handleApplyBundle = (lines: BundleApplyLine[], bundle: Bundle) => {
    const existingRows = getValues('items').map((row) => ({
      itemId: row.item?.id,
      quantity: row.quantity,
    }));
    const { updates, appends, fills, combined, appliedCount, skippedCount } =
      computePhysicalBundleApply(existingRows, lines, RETURN_ORDER_MAX_ITEMS);
    for (const update of updates) {
      setValue(`items.${update.index}.quantity`, update.quantity, {
        shouldValidate: true,
      });
    }
    for (const fill of fills) {
      setValue(`items.${fill.index}.item`, fill.line.item, {
        shouldValidate: true,
      });
      setValue(`items.${fill.index}.quantity`, fill.line.quantity, {
        shouldValidate: true,
      });
    }
    for (const appendLine of appends) {
      append({
        item: appendLine.item,
        quantity: appendLine.quantity,
      });
    }
    showToast({
      element: buildBundleAppliedToast({
        lineCount: appliedCount,
        bundleName: bundle.name,
        combined,
        skippedCount,
      }),
      severity: 'success',
    });
  };

  const onFormSubmit = (data: FormData) => {
    props.onSubmit(data);
  };

  /**
   * Formats the available quantity text for a return order item.
   * Shows total available quantity for both parts and products.
   */
  const getAvailableText = (itemId: string | undefined): string => {
    if (!itemId) {
      return '\u00A0'; // Non-breaking space when no item selected
    }

    if (!selectedFromLocation) {
      return "Select 'from' location to show available";
    }

    if (isLoadingInventory || isFetchingInventory || !inventoryMap) {
      return 'Loading...';
    }

    const quantity =
      inventoryMap.get(selectedFromLocation.id)?.get(itemId) ?? 0;
    return `${formatNumber(quantity)} available`;
  };

  // Get list of selected items to hide from other selectors
  const selectedItems: SparseItem[] = currentItems
    .filter(
      (entry): entry is ItemFormEntry & { item: SparseItem } =>
        entry.item !== null
    )
    .map((entry) => entry.item);

  return (
    <form id={props.formId} onSubmit={handleSubmit(onFormSubmit)}>
      <Grid container spacing={2}>
        {/* Return type */}
        <Grid item xs={12} md={6}>
          <Controller
            name="returnType"
            control={control}
            render={({ field, fieldState }) => (
              <FormControl fullWidth error={!!fieldState.error}>
                <InputLabel id="return-type-label">Reason type</InputLabel>
                <Select
                  size="small"
                  labelId="return-type-label"
                  label="Reason type"
                  {...field}
                  value={field.value ?? ''}
                >
                  {returnOrderReasonSchema.options.map((reason) => (
                    <MenuItem key={reason} value={reason}>
                      {RETURN_ORDER_REASON_LABELS[reason]}
                    </MenuItem>
                  ))}
                </Select>
                {fieldState.error && (
                  <FormHelperText>{fieldState.error.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>

        {/* Return reason (required when type is 'other') */}
        <Grid item xs={12} md={6}>
          <FormTextField
            fullWidth
            name="returnReason"
            label={
              watchedType === 'other'
                ? 'Return reason (required)'
                : 'Return reason'
            }
            control={control}
            isMultiline
            minimumRows={1}
            placeholder={
              watchedType === 'other'
                ? 'Please describe the reason for return'
                : 'Optional notes about the return type'
            }
          />
        </Grid>

        {/* From location */}
        <Grid item xs={12} md={6}>
          <FormLocationSearch
            name="fromLocation"
            control={control}
            label="From location"
            grouping={['non-inventory']}
            disabled={isProcessed}
            onCreate={(name: string) => {
              const url = getNewLocationURL(name);
              window.open(url, '_blank');
            }}
          />
        </Grid>

        {/* To location */}
        <Grid item xs={12} md={6}>
          <FormLocationSearch
            name="toLocation"
            control={control}
            label="To location"
            grouping={['inventory']}
            disabled={isProcessed}
            onCreate={(name: string) => {
              const url = getNewLocationURL(name);
              window.open(url, '_blank');
            }}
          />
        </Grid>

        {/* Promise ship date */}
        <Grid item xs={12} md={6}>
          <FormDatePicker
            name="promiseShipDate"
            label="Promise ship date"
            control={control}
            isClearable
            monthsToShow={2}
          />
          <DateShortcutButtons
            shortcuts={PROMISE_SHIP_DATE_SHORTCUTS}
            getAnchor={() => DateTime.now()}
            anchorName="today"
            onSelect={(date) =>
              setValue('promiseShipDate', date, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
          />
        </Grid>

        {/* Promise delivery date */}
        <Grid item xs={12} md={6}>
          <FormDatePicker
            name="promiseDeliveryDate"
            label="Promise delivery date"
            control={control}
            isClearable
            monthsToShow={2}
          />
          <DateShortcutButtons
            shortcuts={getPromiseDeliveryDateShortcuts(
              selectedPromiseShipDate !== null
            )}
            getAnchor={() => selectedPromiseShipDate ?? DateTime.now()}
            anchorName={
              selectedPromiseShipDate ? 'the promise ship date' : 'today'
            }
            onSelect={(date) =>
              setValue('promiseDeliveryDate', date, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
          />
        </Grid>

        {/* Transport reference */}
        <Grid item xs={12}>
          {!isTransportRefOpen ? (
            <Stack direction="row" justifyContent="flex-end">
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                onClick={() => setIsTransportRefOpen(true)}
                sx={{ cursor: 'pointer' }}
              >
                <Typography variant="body2" color="primary.main">
                  Add transport reference
                </Typography>
                <Tooltip
                  arrow
                  title="Optional master tracking or carrier reference for this return order"
                >
                  <InfoOutlined
                    sx={{ fontSize: 16, color: 'text.secondary' }}
                  />
                </Tooltip>
              </Stack>
            </Stack>
          ) : (
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="subtitle2">
                    Transport reference
                  </Typography>
                  <Tooltip
                    arrow
                    title="Optional master tracking or carrier reference for this return order"
                  >
                    <InfoOutlined
                      sx={{ fontSize: 16, color: 'text.secondary' }}
                    />
                  </Tooltip>
                </Stack>
                <IconButton size="small" onClick={handleCloseTransportRef}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormTextField
                    fullWidth
                    name="referenceNumber"
                    label="Transport reference"
                    control={control}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormShippingCarrierSearch
                    name="carrier"
                    label="Carrier"
                    control={control}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Collapse in={isOtherCarrier}>
                    <FormTextField
                      fullWidth
                      name="otherCarrier"
                      label="Name"
                      control={control}
                    />
                  </Collapse>
                </Grid>
              </Grid>
            </Box>
          )}
        </Grid>

        {/* Return order notes */}
        <Grid item xs={12}>
          <FormTextField
            fullWidth
            name="orderNotes"
            label="Return order notes"
            control={control}
            isMultiline
            minimumRows={3}
            placeholder="Notes about this return order"
          />
        </Grid>

        {/* File upload section */}
        {props.fileUploadProps && (
          <Grid item xs={12} md={12} sx={{ display: 'flex' }}>
            <FileDropZone
              files={props.fileUploadProps.files}
              onFilesSelected={props.fileUploadProps.onFilesSelected}
              onRemoveFile={props.fileUploadProps.onRemoveFile}
              disabled={props.fileUploadProps.disabled}
            />
          </Grid>
        )}

        {/* Items section */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="subtitle1">Return order items</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                disabled={
                  itemsField.length >= RETURN_ORDER_MAX_ITEMS || isProcessed
                }
              >
                Add item
              </Button>
              {/* Stays enabled at the row cap: applying a bundle can still
                  auto-sum quantities into existing rows without adding rows */}
              <Button
                variant="outlined"
                startIcon={<LibraryAddOutlined />}
                onClick={applyBundleDialog.open}
                disabled={isProcessed}
              >
                Add bundle
              </Button>
            </Stack>
          </Stack>

          {itemsField.map((field, index) => {
            const currentValue = currentItems[index];

            return (
              <Stack
                key={field.id}
                direction="row"
                spacing={2}
                mb={2}
                alignItems="flex-start"
              >
                <Box sx={{ flex: 1 }}>
                  <Controller
                    control={control}
                    name={`items.${index}`}
                    render={({ field: itemField, fieldState }) => {
                      const itemError = (
                        fieldState.error as {
                          item?: { message?: string };
                        }
                      )?.item;

                      return (
                        <ItemSearch
                          label="Item"
                          value={currentValue?.item ?? null}
                          disabled={isProcessed}
                          physicalOnly={true}
                          onChange={(value) => {
                            itemField.onChange({
                              item: value,
                              quantity: currentValue?.quantity ?? 1,
                            });
                          }}
                          hiddenItems={selectedItems.filter(
                            (item) => item.id !== currentValue?.item?.id
                          )}
                          error={!!itemError}
                          helperText={itemError?.message}
                        />
                      );
                    }}
                  />
                </Box>

                <Stack
                  direction="column"
                  spacing={0.25}
                  sx={{ width: '200px' }}
                >
                  <FormNumberTextField
                    name={`items.${index}.quantity`}
                    label="Quantity"
                    control={control}
                    numeric
                    disabled={isProcessed}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textAlign: 'right' }}
                  >
                    {getAvailableText(currentValue?.item?.id)}
                  </Typography>
                </Stack>

                <IconButton
                  onClick={() => remove(index)}
                  disabled={itemsField.length === 1 || isProcessed}
                  sx={{ mt: 1 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            );
          })}
        </Grid>
      </Grid>
      <ApplyBundleDialog
        isOpen={applyBundleDialog.isOpen}
        onClose={applyBundleDialog.close}
        onApply={handleApplyBundle}
      />
    </form>
  );
}
